import { useState } from "react";
import { useBranch } from "@/context/branch";
import { useToast } from "@/hooks/use-toast";
import { useListPurchases, useUpdatePurchase, useListSuppliers, useListProducts, getListPurchasesQueryKey } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Loader2, Plus, Trash2, CheckCircle, Package, PenLine, Truck, ChevronsUpDown, Check, DollarSign, AlertCircle, Search, MoreVertical, Eye, Pencil, Clock } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { useMutation } from "@tanstack/react-query";
import { formatCurrency } from "@/lib/currency";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  pending: "secondary",
  received: "default",
  cancelled: "destructive",
};

const PAY_STATUS_VARIANTS: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  paid: "default",
  partial: "secondary",
  unpaid: "outline",
};

type ItemMode = "existing" | "custom";
type PurchaseItem = {
  mode: ItemMode;
  productId: number;
  productName: string;
  quantity: number;
  unitCost: number;
};

type PayDialog = {
  id: number;
  orderNumber: string;
  supplierName: string;
  totalAmount: number;
  amountPaid: number;
  balance: number;
};

export default function Purchases() {
  const queryClient = useQueryClient();
  const { activeBranchId, activeBranch } = useBranch();
  const { toast } = useToast();
  const { data: purchases, isLoading } = useListPurchases({
    branchId: activeBranchId ?? undefined,
  });
  const { data: suppliers } = useListSuppliers();
  const { data: products } = useListProducts({
    branchId: activeBranchId ?? undefined,
  });
  const updatePurchase = useUpdatePurchase();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [items, setItems] = useState<PurchaseItem[]>([]);
  const [expectedDate, setExpectedDate] = useState("");
  const [notes, setNotes] = useState("");
  const [openCombobox, setOpenCombobox] = useState<number | null>(null);

  // Payment recording
  const [payDialog, setPayDialog] = useState<PayDialog | null>(null);
  const [payAmount, setPayAmount] = useState("");
  const [payError, setPayError] = useState("");
  const [search, setSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState<"all" | "paid" | "unpaid">("all");
  const [detailsPurchase, setDetailsPurchase] = useState<any | null>(null);

  const createPurchaseMutation = useMutation({
    mutationFn: async (payload: any) => {
      const res = await fetch(`${BASE}/api/purchases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to create purchase");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListPurchasesQueryKey() });
      setIsCreateOpen(false);
      setSupplierId(""); setSupplierOpen(false); setItems([]); setExpectedDate(""); setNotes("");
    },
    onError: (e: Error) => toast({ title: e.message || "Failed to create purchase order", variant: "destructive" }),
  });

  const payMutation = useMutation({
    mutationFn: async ({ id, amount }: { id: number; amount: number }) => {
      const res = await fetch(`${BASE}/api/purchases/${id}/pay`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amount }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to record payment");
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListPurchasesQueryKey() });
      setPayDialog(null);
      setPayAmount("");
      setPayError("");
    },
    onError: (err: Error) => setPayError(err.message),
  });

  const addItem = (mode: ItemMode = "existing") => {
    setItems(prev => [...prev, { mode, productId: 0, productName: "", quantity: 1, unitCost: 0 }]);
  };

  const updateItem = (idx: number, updates: Partial<PurchaseItem>) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const merged = { ...item, ...updates };
      if (updates.productId !== undefined && updates.productId > 0) {
        const p = products?.find(p => p.id === updates.productId);
        if (p) merged.productName = p.name;
        merged.unitCost = Number((products?.find(p => p.id === updates.productId) as any)?.costPrice ?? 0);
      }
      return merged;
    }));
  };

  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));

  const total = items.reduce((s, i) => s + i.quantity * i.unitCost, 0);

  const isValid = supplierId && items.length > 0 && items.every(i => {
    if (i.mode === "custom") return i.productName.trim().length > 0;
    return i.productId > 0;
  });

  const handleSubmit = () => {
    if (!isValid) return;
    createPurchaseMutation.mutate({
      supplierId: Number(supplierId),
      items: items.map(i => ({
        productId: i.mode === "existing" ? i.productId : 0,
        customProductName: i.mode === "custom" ? i.productName : undefined,
        quantity: i.quantity,
        unitCost: i.unitCost,
      })),
      notes: notes || undefined,
      expectedDate: expectedDate || undefined,
      branchId: activeBranchId ?? undefined,
    });
  };

  const markReceived = (id: number) => {
    updatePurchase.mutate({ id, data: { status: "received" } }, {
      onSuccess: () => queryClient.invalidateQueries({ queryKey: getListPurchasesQueryKey() }),
      onError: () => toast({ title: "Failed to mark as received", variant: "destructive" }),
    });
  };

  const openPayDialog = (p: any) => {
    setPayAmount("");
    setPayError("");
    setPayDialog({
      id: p.id,
      orderNumber: p.orderNumber,
      supplierName: p.supplierName,
      totalAmount: Number(p.totalAmount),
      amountPaid: Number(p.amountPaid ?? 0),
      balance: Number(p.balance ?? p.totalAmount),
    });
  };

  const handlePay = () => {
    if (!payDialog) return;
    const amount = Number(payAmount);
    if (!amount || amount <= 0) { setPayError("Enter a valid amount"); return; }
    if (amount > payDialog.balance) { setPayError(`Amount exceeds balance of ${formatCurrency(payDialog.balance)}`); return; }
    payMutation.mutate({ id: payDialog.id, amount });
  };

  const getPaymentStatus = (purchase: any): "paid" | "partial" | "unpaid" => {
    const status = purchase.paymentStatus;
    if (status === "paid" || status === "partial" || status === "unpaid") return status;
    const total = Number(purchase.totalAmount) || 0;
    const paid = Number(purchase.amountPaid) || 0;
    return paid >= total ? "paid" : paid > 0 ? "partial" : "unpaid";
  };

  const getBalance = (purchase: any) =>
    Math.max(0, Number(purchase.balance ?? Number(purchase.totalAmount || 0) - Number(purchase.amountPaid || 0)));

  const paymentStatusLabel = (status: "paid" | "partial" | "unpaid") =>
    status === "paid" ? "Fully Paid" : status === "partial" ? "Partial" : "Unpaid";

  const totalPurchases = (purchases ?? []).reduce((sum, purchase: any) => sum + Number(purchase.totalAmount || 0), 0);
  const unpaidBalance = (purchases ?? []).reduce(
    (sum, purchase: any) => sum + (purchase.supplierBillId ? 0 : getBalance(purchase)),
    0,
  );
  const pendingOrders = (purchases ?? []).filter((purchase: any) => purchase.status === "pending").length;
  const normalizedSearch = search.trim().toLowerCase();
  const filteredPurchases = (purchases ?? []).filter((purchase: any) => {
    const matchesSearch = !normalizedSearch
      || String(purchase.orderNumber ?? "").toLowerCase().includes(normalizedSearch)
      || String(purchase.supplierName ?? "").toLowerCase().includes(normalizedSearch);
    const status = getPaymentStatus(purchase);
    const matchesPayment = paymentFilter === "all"
      || (paymentFilter === "paid" ? status === "paid" : status !== "paid");
    return matchesSearch && matchesPayment;
  });

  return (
    <div className="space-y-6 pb-16">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Purchases</h1>
          <p className="text-muted-foreground mt-1">
            {activeBranch ? `Track incoming stock for ${activeBranch.name}.` : "Track incoming stock across all locations."}
          </p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="w-4 h-4 mr-2" /> New Order</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create Purchase Order</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Supplier</label>
                  <Popover open={supplierOpen} onOpenChange={setSupplierOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" role="combobox" className="w-full mt-1 justify-between font-normal">
                        <span className="truncate">
                          {supplierId
                            ? suppliers?.find(s => String(s.id) === supplierId)?.name ?? "Select supplier"
                            : "Select supplier"}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[260px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search supplier..." />
                        <CommandList className="max-h-[200px] overflow-y-auto">
                          <CommandEmpty>No supplier found.</CommandEmpty>
                          <CommandGroup>
                            {suppliers?.map(s => (
                              <CommandItem
                                key={s.id}
                                value={s.name}
                                onSelect={() => { setSupplierId(String(s.id)); setSupplierOpen(false); }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", supplierId === String(s.id) ? "opacity-100" : "opacity-0")} />
                                {s.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="text-sm font-medium">Expected Date</label>
                  <Input type="date" value={expectedDate} onChange={e => setExpectedDate(e.target.value)} className="mt-1" />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Items</label>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => addItem("existing")} className="gap-1 h-7 text-xs">
                      <Package className="w-3 h-3" /> From Inventory
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => addItem("custom")} className="gap-1 h-7 text-xs">
                      <PenLine className="w-3 h-3" /> New Product
                    </Button>
                  </div>
                </div>

                {items.length === 0 && (
                  <div className="text-center py-4 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
                    Add items from your inventory, or type in a new product name
                  </div>
                )}

                {items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2 rounded-lg bg-muted/30 border">
                    <div className="col-span-5">
                      {item.mode === "existing" ? (
                        <>
                          <label className="text-xs text-muted-foreground">Product (from inventory)</label>
                          <Popover open={openCombobox === idx} onOpenChange={o => setOpenCombobox(o ? idx : null)}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                className="w-full h-8 text-sm mt-0.5 justify-between font-normal"
                              >
                                <span className="truncate">
                                  {item.productId
                                    ? products?.find(p => p.id === item.productId)?.name ?? "Select product"
                                    : "Select product"}
                                </span>
                                <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[260px] p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search products..." className="h-8" />
                                <CommandList className="max-h-[200px] overflow-y-auto">
                                  <CommandEmpty>No products found.</CommandEmpty>
                                  <CommandGroup>
                                    {products?.map(p => (
                                      <CommandItem
                                        key={p.id}
                                        value={p.name}
                                        onSelect={() => {
                                          updateItem(idx, { productId: p.id });
                                          setOpenCombobox(null);
                                        }}
                                      >
                                        <Check className={cn("mr-2 h-3.5 w-3.5", item.productId === p.id ? "opacity-100" : "opacity-0")} />
                                        {p.name}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </>
                      ) : (
                        <>
                          <label className="text-xs text-muted-foreground">Product name (new)</label>
                          <Input
                            placeholder="e.g. Indomie Noodles x48"
                            value={item.productName}
                            onChange={e => updateItem(idx, { productName: e.target.value })}
                            className="h-8 text-sm mt-0.5"
                          />
                        </>
                      )}
                    </div>
                    <div className="col-span-2">
                      <label className="text-xs text-muted-foreground">Qty</label>
                      <Input
                        type="number" min="1"
                        value={item.quantity}
                        onChange={e => updateItem(idx, { quantity: Number(e.target.value) })}
                        className="h-8 text-sm mt-0.5"
                      />
                    </div>
                    <div className="col-span-3">
                      <label className="text-xs text-muted-foreground">Unit Cost (₦)</label>
                      <Input
                        type="number" step="0.01" min="0"
                        placeholder="0.00"
                        value={item.unitCost === 0 ? "" : item.unitCost}
                        onChange={e => updateItem(idx, { unitCost: Number(e.target.value) || 0 })}
                        className="h-8 text-sm mt-0.5"
                      />
                    </div>
                    <div className="col-span-1 text-sm font-medium text-right pb-1">
                      ₦{(item.quantity * item.unitCost).toLocaleString("en-NG", { minimumFractionDigits: 0 })}
                    </div>
                    <div className="col-span-1 pb-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeItem(idx)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}

                {items.length > 0 && (
                  <div className="text-right text-sm font-bold border-t pt-2">
                    Total: <span className="text-primary text-base">₦{total.toLocaleString("en-NG", { minimumFractionDigits: 2 })}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="text-sm font-medium">Notes</label>
                <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes..." className="mt-1" />
              </div>

              <Button
                className="w-full"
                onClick={handleSubmit}
                disabled={createPurchaseMutation.isPending || !isValid}
              >
                {createPurchaseMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Purchase Order
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Purchases</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(totalPurchases)}</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center">
              <Package className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Unpaid Balance</p>
              <p className={`text-2xl font-bold mt-1 ${unpaidBalance > 0 ? "text-amber-600" : "text-emerald-600"}`}>
                {formatCurrency(unpaidBalance)}
              </p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center">
              <DollarSign className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Pending Orders</p>
              <p className="text-2xl font-bold mt-1">{pendingOrders}</p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-violet-500/10 text-violet-600 flex items-center justify-center">
              <Clock className="w-5 h-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between rounded-xl border bg-card p-3">
        <div className="relative flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by order # or supplier..."
            className="pl-9"
          />
        </div>
        <Select value={paymentFilter} onValueChange={value => setPaymentFilter(value as "all" | "paid" | "unpaid")}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Payment status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All payments</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-md overflow-hidden">
        <div className="overflow-y-auto max-h-[600px]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead>Order #</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Expected</TableHead>
              <TableHead>Fulfillment Status</TableHead>
              <TableHead>Payment Status</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Paid / Balance</TableHead>
              <TableHead className="w-[170px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>
            ) : filteredPurchases.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-16">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                      <Truck className="w-6 h-6 text-muted-foreground/50" />
                    </div>
                    <div>
                      <p className="font-semibold">{purchases?.length ? "No matching purchase orders" : "No purchase orders yet"}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {purchases?.length ? "Try a different search or payment filter." : "Create a purchase order to track stock coming in from suppliers."}
                      </p>
                    </div>
                    {!purchases?.length && (
                      <>
                        <Button size="sm" className="gap-2 mt-1" onClick={() => setIsCreateOpen(true)}>
                          <Plus className="w-4 h-4" /> Create First Purchase Order
                        </Button>
                      <p className="text-xs text-muted-foreground/60">Add your suppliers first so you can select them here.</p>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredPurchases.slice().reverse().map((p: any) => (
              <TableRow key={p.id}>
                <TableCell className="font-mono text-sm">{p.orderNumber}</TableCell>
                <TableCell className="font-medium">{p.supplierName}</TableCell>
                <TableCell>{p.expectedDate ? format(new Date(p.expectedDate), "MMM d, yyyy") : "—"}</TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANTS[p.status] || "outline"}>
                    {p.status === "received" ? "Received" : p.status === "cancelled" ? "Cancelled" : "Pending"}
                  </Badge>
                </TableCell>
                <TableCell>
                  {p.supplierBillId
                    ? <Badge variant="outline">Bill managed</Badge>
                    : <Badge variant={PAY_STATUS_VARIANTS[getPaymentStatus(p)] || "outline"}>{paymentStatusLabel(getPaymentStatus(p))}</Badge>}
                </TableCell>
                <TableCell className="text-right font-medium">
                  {formatCurrency(Number(p.totalAmount))}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex flex-col items-end gap-0.5">
                    {p.supplierBillId ? <>
                      <span className="text-xs text-muted-foreground">Paid on PO (carried): {formatCurrency(Number(p.amountPaid ?? 0))}</span>
                      <span className="text-xs font-medium text-emerald-700">Remaining payable tracked on bill</span>
                    </> : <>
                      <span className="text-xs text-muted-foreground">Paid: {formatCurrency(Number(p.amountPaid ?? 0))}</span>
                      {getBalance(p) > 0 && <span className="text-xs text-muted-foreground">Balance: {formatCurrency(getBalance(p))}</span>}
                    </>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    {getBalance(p) > 0 && !p.supplierBillId && (
                      <Button variant="outline" size="sm" onClick={() => openPayDialog(p)} className="h-8 px-2.5 text-xs gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50">
                        <DollarSign className="w-3 h-3" /> Pay
                      </Button>
                    )}
                    {p.supplierBillId && (
                      <span className="inline-flex items-center gap-1 rounded-md border border-[#c9ddd5] bg-[#eaf4ef] px-2 py-1.5 text-[11px] font-medium text-[#28644b]" title="This purchase order payable is now managed by its supplier bill">
                        <CheckCircle className="h-3 w-3" /> Managed by bill {p.supplierBillNumber || `#${p.supplierBillId}`}
                      </span>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Actions for ${p.orderNumber}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>{p.orderNumber}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => setDetailsPurchase(p)}>
                          <Eye /> View order details
                        </DropdownMenuItem>
                        {p.status === "pending" && (
                          <DropdownMenuItem onSelect={() => markReceived(p.id)} disabled={updatePurchase.isPending}>
                            <Pencil /> Mark as received
                          </DropdownMenuItem>
                        )}
                        {getBalance(p) > 0 && !p.supplierBillId && (
                          <DropdownMenuItem onSelect={() => openPayDialog(p)}>
                            <DollarSign /> Record payment
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      <Dialog open={!!detailsPurchase} onOpenChange={open => !open && setDetailsPurchase(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Purchase Order Details</DialogTitle>
          </DialogHeader>
          {detailsPurchase && (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted/40 p-4 space-y-2 text-sm">
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Order #</span><span className="font-mono font-medium">{detailsPurchase.orderNumber}</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Supplier</span><span className="font-medium text-right">{detailsPurchase.supplierName}</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Expected</span><span>{detailsPurchase.expectedDate ? format(new Date(detailsPurchase.expectedDate), "MMM d, yyyy") : "—"}</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Fulfillment</span><Badge variant={STATUS_VARIANTS[detailsPurchase.status] || "outline"}>{detailsPurchase.status === "received" ? "Received" : detailsPurchase.status === "cancelled" ? "Cancelled" : "Pending"}</Badge></div>
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Payment</span>{detailsPurchase.supplierBillId ? <Badge variant="outline">Bill managed</Badge> : <Badge variant={PAY_STATUS_VARIANTS[getPaymentStatus(detailsPurchase)] || "outline"}>{paymentStatusLabel(getPaymentStatus(detailsPurchase))}</Badge>}</div>
                <div className="flex justify-between gap-4 border-t pt-2"><span className="font-medium">Total</span><span className="font-bold">{formatCurrency(Number(detailsPurchase.totalAmount))}</span></div>
                {detailsPurchase.supplierBillId
                  ? <div className="flex justify-between gap-4"><span className="text-muted-foreground">PO balance</span><span>Tracked on bill {detailsPurchase.supplierBillNumber || `#${detailsPurchase.supplierBillId}`}</span></div>
                  : <div className="flex justify-between gap-4"><span className="text-muted-foreground">Balance</span><span>{formatCurrency(getBalance(detailsPurchase))}</span></div>}
                {detailsPurchase.supplierBillId && <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">Payable managed by supplier bill {detailsPurchase.supplierBillNumber || `#${detailsPurchase.supplierBillId}`}. Record future payments against that bill, not this purchase order.</div>}
              </div>
              {detailsPurchase.notes && <p className="text-sm text-muted-foreground"><span className="font-medium text-foreground">Notes:</span> {detailsPurchase.notes}</p>}
              <div className="flex gap-2">
                {detailsPurchase.status === "pending" && (
                  <Button variant="outline" onClick={() => { markReceived(detailsPurchase.id); setDetailsPurchase(null); }} className="flex-1 gap-2">
                    <CheckCircle className="w-4 h-4" /> Mark as received
                  </Button>
                )}
                {getBalance(detailsPurchase) > 0 && !detailsPurchase.supplierBillId && (
                  <Button onClick={() => { openPayDialog(detailsPurchase); setDetailsPurchase(null); }} className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-700">
                    <DollarSign className="w-4 h-4" /> Pay balance
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={!!payDialog} onOpenChange={open => { if (!open) { setPayDialog(null); setPayAmount(""); setPayError(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" /> Record Supplier Payment
            </DialogTitle>
          </DialogHeader>
          {payDialog && (
            <div className="space-y-4 pt-1">
              <div className="rounded-lg bg-muted/50 p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Order</span><span className="font-mono font-medium">{payDialog.orderNumber}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Supplier</span><span className="font-medium">{payDialog.supplierName}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span>{formatCurrency(payDialog.totalAmount)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Already paid</span><span className="text-emerald-600">{formatCurrency(payDialog.amountPaid)}</span></div>
                <div className="flex justify-between font-semibold border-t pt-2"><span>Balance owed</span><span className="text-destructive">{formatCurrency(payDialog.balance)}</span></div>
              </div>
              <div>
                <label className="text-sm font-medium">Payment Amount (₦)</label>
                <Input
                  type="number" min="1" step="0.01"
                  placeholder="0.00"
                  value={payAmount}
                  onChange={e => { setPayAmount(e.target.value); setPayError(""); }}
                  className="mt-1.5 text-lg font-semibold"
                  autoFocus
                />
                <div className="flex gap-2 mt-2">
                  <Button
                    variant="outline" size="sm"
                    className="text-xs h-7"
                    onClick={() => setPayAmount(String(payDialog.balance))}
                  >
                    Full balance ({formatCurrency(payDialog.balance)})
                  </Button>
                </div>
              </div>
              {payError && (
                <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 rounded-md p-3">
                  <AlertCircle className="w-4 h-4 shrink-0" />{payError}
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setPayDialog(null); setPayAmount(""); setPayError(""); }} className="flex-1">Cancel</Button>
                <Button onClick={handlePay} disabled={payMutation.isPending || !payAmount} className="flex-1 gap-2 bg-emerald-600 hover:bg-emerald-700">
                  {payMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <DollarSign className="w-4 h-4" />}
                  Record Payment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
